import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes, faFileExcel, faUpload, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  ValueGetterParams,
} from 'ag-grid-community';
import { AgGridThemeService } from '../../_services/ag-grid-theme.service';
import { AG_GRID_LOCALE_DE } from '../../_helpers/aggrid.locale.de';
import {
  ExcelApplyConfigSpec,
  ExcelApplySplitGroup,
  ExcelChange,
  ExcelPreviewResult,
  ExcelPreviewRow,
  ExcelTemplateSource,
  OperationRequirementService,
} from '../../_services/operation-requirement.service';
import { ProjectService } from '../../_services/project.service';
import { ShippingConfigService, ShippingConfigDefaultPreview } from '../../_services/shipping-config.service';
import {
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
} from '../../_models/shipping-config.interface';
import { ShippingConfigFormComponent } from '../_shared/shipping-config-form/shipping-config-form.component';
import { ShippingConfigHeaderComponent } from './shipping-config-header.component';
import {
  BulkExcelSplitModel,
  SplitColumn,
  SplitGridRow,
  SplitState,
  withCell,
  withoutRef,
} from './bulk-excel-split-model';

interface ProjectOption {
  id: number;
  name: string;
}

type Phase = 'setup' | 'uploading' | 'review' | 'applying' | 'done';

export type AutoCreateCategory = 'internal' | 'external_staging' | 'external_material' | 'external_other';

/**
 * Bulk-Edit-Popup fuer Versand-/Staging-Artikelmengen ueber mehrere Aufträge.
 * Vier Phasen: Setup (Projekt + Vorlagen-Quelle waehlen, Excel exportieren) →
 * Upload (Excel zurueck hochladen) → Review (Diff + Inline-Split in AG-Grid) →
 * Applying/Done. Wird als Dialog von TicketOperations.component eingebunden.
 *
 * Review-Grid (Phase 3 „Zeile pro Config"): pro Auftrag eine Zeile je Versand-
 * konfiguration plus eine „offen"-Summenzeile; die Artikel-Spalten sind pro
 * Config editierbar (Mengen-Split). Configs werden direkt im Grid angelegt/
 * bearbeitet; beim Apply gehen Mengen-Diffs, Configs UND der Split in EINEM
 * atomaren Request raus.
 */
@Component({
  standalone: true,
  selector: 'hawk-operations-bulk-excel',
  imports: [CommonModule, FormsModule, FontAwesomeModule, AgGridAngular, ShippingConfigFormComponent],
  templateUrl: './operations-bulk-excel.component.html',
  // ViewEncapsulation.None, weil AG-Grid-Cellklassen ausserhalb der Komponenten-
  // Scope-Attribute gerendert werden — Tailwind ist hier nicht anwendbar.
  // Klassennamen sind explizit auf die Diff-Tabelle eingegrenzt.
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .bulk-excel-hawk-link { color: #2563eb; cursor: pointer; text-decoration: underline; }
    .bulk-excel-hawk-link:hover { color: #1d4ed8; }
    .bulk-excel-config-cell { cursor: pointer; }
    .bulk-excel-config-cell:hover { filter: brightness(0.97); }
    .bulk-excel-muted { color: #6b7280; font-style: italic; }
    .bulk-op-separator { border-top: 2px solid rgba(0,0,0,0.18); }
    /* Auftragsspalte: durchgehender Hintergrund ueber alle Zeilen eines
       Auftrags, damit der Block optisch zusammenhaengt. Die 2px-Linie aus
       .bulk-op-separator trennt die Auftraege weiterhin. */
    /* Auftragsspalte als Zeilenkopf: dieselbe Farbe wie der Spalten-Header.
       DECKEND, nicht halbtransparent — sonst schlaegt die Zebra-Streifung des
       Grids durch und jede zweite Zeile wird dunkler. */
    .bulk-op-order-cell { background: var(--ag-header-background-color, #f8f9fa) !important; }
    .bulk-op-inner-row { border-bottom: none !important; }
    .bulk-op-inner-row .ag-cell { border-bottom: none !important; }
  `],
})
export class OperationsBulkExcelComponent implements OnInit {
  @Input() operationIds: number[] = [];
  @Input() projectId: number | null = null;
  @Output() closeDialog = new EventEmitter<void>();
  @Output() imported = new EventEmitter<void>();

  private excelService = inject(OperationRequirementService);
  private projectService = inject(ProjectService);
  private shippingConfigService = inject(ShippingConfigService);

  private static readonly CONFIG_COL_ID = 'config';

  // ===========================================================================
  // Phasen-/Basis-State
  // ===========================================================================
  protected phase = signal<Phase>('setup');
  protected selectedProjectId = signal<number | null>(null);
  protected templateSource = signal<ExcelTemplateSource>('project');
  protected projects = signal<ProjectOption[]>([]);
  protected uploadedFile = signal<File | null>(null);
  // Sperrt den "Hochladen & prüfen"-Button waehrend des laufenden Preview-Requests
  // (verhindert Mehrfach-Requests bei Doppel-Klick).
  protected uploadRunning = signal<boolean>(false);
  protected previewResult = signal<ExcelPreviewResult | null>(null);
  protected applyResult = signal<{ created: number; updated: number; deleted: number; errors: string[] } | null>(null);
  protected errorMessage = signal<string | null>(null);

  protected gridTheme = inject(AgGridThemeService).getTheme();
  protected gridLocale = AG_GRID_LOCALE_DE;
  private gridApi: GridApi<SplitGridRow> | null = null;

  readonly faTimes = faTimes;
  readonly faExcel = faFileExcel;
  readonly faUpload = faUpload;
  readonly faCheck = faCheck;
  readonly faWarn = faExclamationTriangle;

  // ===========================================================================
  // Split-Modell + State
  // ===========================================================================
  protected splitModel = computed<BulkExcelSplitModel | null>(() => {
    const r = this.previewResult();
    return r ? new BulkExcelSplitModel(r) : null;
  });

  protected splitColumns = computed<SplitColumn[]>(() => this.splitModel()?.columns() ?? []);

  // Lokaler Mengen-Split: opId → configRef → colId → quantity. Immutable
  // aktualisiert (withCell/withoutRef), damit das Signal sauber triggert.
  protected splitState = signal<SplitState>(new Map());

  // Pending Versand-Configs (Form-Daten) je Pending-Key `${opId}#${ref}`:
  //   ref = bestehende config_id (Edit) ODER `new<N>` (neue Config).
  protected pendingShippingConfigs = signal<Map<string, ShippingConfigFormData>>(new Map());
  private newConfigSeq = 1;

  constructor() {
    // Zell-Werte haengen am splitState (kein rowData-Feld) → bei jeder Aenderung
    // die Artikel-Zellen neu rendern. Die Zeilenmenge (Configs) haengt an
    // pendingShippingConfigs und wird ueber das [rowData]-Binding diff't.
    effect(() => {
      this.splitState();
      this.pendingShippingConfigs();
      this.gridApi?.refreshCells({ force: true });
      // Header traegt den „fehlende Configs"-Counter → bei Aenderung neu rendern.
      this.gridApi?.refreshHeader();
    });
  }

  protected onGridReady = (event: GridReadyEvent<SplitGridRow>): void => {
    this.gridApi = event.api;
  };

  private pendingKey(opId: number, ref: string): string {
    return `${opId}#${ref}`;
  }

  // ref-Teile der pending-NEUEN Configs eines Auftrags (`new<N>`).
  private pendingNewRefs(opId: number): string[] {
    const prefix = `${opId}#`;
    const refs: string[] = [];
    for (const key of this.pendingShippingConfigs().keys()) {
      if (!key.startsWith(prefix)) continue;
      const ref = key.slice(prefix.length);
      if (ref.startsWith('new')) refs.push(ref);
    }
    return refs;
  }

  // Alle Config-Refs eines Auftrags in Anzeigereihenfolge: offene bestehende
  // (nach id), pending-neue, zuletzt die abgeschlossenen (read-only).
  private opConfigRefs(row: ExcelPreviewRow): string[] {
    const m = this.splitModel();
    if (!m) return [];
    const existing = m.existingConfigs(row).map((c) => String(c.id));
    const completed = m.completedConfigs(row).map((c) => String(c.id));
    return [...existing, ...this.pendingNewRefs(row.operation_id), ...completed];
  }

  // Editierbare Config-Refs (offene bestehende + pending-neue) in Reihenfolge —
  // ohne die read-only abgeschlossenen. Anker fuer den „+ weitere Config"-Button.
  private editableConfigRefs(row: ExcelPreviewRow): string[] {
    const m = this.splitModel();
    if (!m) return [];
    return [...m.existingConfigs(row).map((c) => String(c.id)), ...this.pendingNewRefs(row.operation_id)];
  }

  private hasEditableConfig(row: ExcelPreviewRow): boolean {
    return this.editableConfigRefs(row).length > 0;
  }

  // ===========================================================================
  // Flache Grid-Zeilen (Zeile pro Config + offen)
  // ===========================================================================
  protected splitRows = computed<SplitGridRow[]>(() => {
    const m = this.splitModel();
    if (!m) return [];
    // pendingShippingConfigs lesen, damit neu angelegte Config-Zeilen erscheinen,
    // und splitState, damit die offen-Zeile beim Verteilen auf-/zugeht.
    this.pendingShippingConfigs();
    const state = this.splitState();
    const out: SplitGridRow[] = [];
    for (const row of m.rows) {
      const refs = this.opConfigRefs(row);
      const opStart = out.length;
      let first = true;
      // Auftragsbedarf (zweite Excel-Zeile) als erste Grid-Zeile — read-only,
      // ausserhalb des Config-Splits. Nur wenn die Datei dafür Mengen bringt.
      if (this.hasNeedQuantities(row)) {
        out.push({ rowId: `${row.operation_id}:need`, operationId: row.operation_id, kind: 'need', configRef: '', isFirstOfOp: first, row });
        first = false;
      }
      for (const ref of refs) {
        out.push({ rowId: `${row.operation_id}:${ref}`, operationId: row.operation_id, kind: 'config', configRef: ref, isFirstOfOp: first, row });
        first = false;
      }
      // „offen"-Zeile nur, wenn der Auftrag (noch) keine Config hat (dann traegt
      // sie die ⚠-Warnung + „+ Config") ODER tatsaechlich ein Rest unverteilt ist.
      const hasOpenPool = m.columns().some((col) => m.poolFor(state, row, col) > 0);
      if (refs.length === 0 || hasOpenPool) {
        out.push({ rowId: `${row.operation_id}:open`, operationId: row.operation_id, kind: 'open', configRef: '', isFirstOfOp: first, row });
      }

      // Nur die letzte Zeile des Auftrags trägt die Trennlinie.
      if (out.length > opStart) out[out.length - 1].isLastOfOp = true;
    }
    return out;
  });

  /**
   * Bekommt dieser Auftrag eine Bedarfszeile?
   *
   * Ja, sobald die Datei ueberhaupt eine Kategorie-Spalte hatte — auch wenn
   * alle Mengen 0 sind. Die Zeile ist dann leer, aber sichtbar: Sonst bliebe
   * unklar, ob der Auftrag keinen Bedarf hat oder die Tabelle ihn nur nicht
   * zeigt, und man vermisst die zweite Zeile aus der Excel.
   *
   * Nein bei Versand-Prioritaet: Dort wird kein Material vor Ort verbraucht,
   * die API lehnt Auftragsbedarf ab und der Export bietet gar keine
   * Bedarfszeile mehr an.
   *
   * Nein bei Alt-Exporten ohne Kategorie-Spalte: Dort fehlt die Bedarfszeile in
   * der Datei, und der Importer liefert ein LEERES need_cells — nicht null.
   */
  private hasNeedQuantities(row: ExcelPreviewRow): boolean {
    if (row.priority_material_shipping) return false;

    return Object.keys(row.need_cells ?? {}).length > 0;
  }

  protected getRowId = (params: { data: SplitGridRow }) => params.data.rowId;

  protected previewDefaultColDef: ColDef = {
    resizable: true,
    suppressMovable: true,
    singleClickEdit: true,
  };

  protected rowClassRules = {
    'bulk-op-separator': (p: { data?: SplitGridRow }) => !!p.data?.isFirstOfOp,
    // Innerhalb eines Auftrags keine waagerechte Linie — die Zeilen gehören
    // zusammen und sollen wie eine wirken.
    'bulk-op-inner-row': (p: { data?: SplitGridRow }) => !p.data?.isLastOfOp,
  };

  // ===========================================================================
  // Spaltendefinitionen
  // ===========================================================================
  protected previewColumnDefs = computed<(ColDef<SplitGridRow> | ColGroupDef<SplitGridRow>)[]>(() => {
    const m = this.splitModel();
    if (!m) return [];

    // Auftrag bleibt links gepinnt, die Artikel-Gruppen scrollen in der Mitte,
    // die Versandkonfig-Spalte ist rechts gepinnt (Wunsch: Mengen-Mittelteil frei
    // scrollbar, Config-Kontext immer sichtbar).
    const cols: (ColDef<SplitGridRow> | ColGroupDef<SplitGridRow>)[] = [
      {
        colId: 'order_nr',
        headerName: 'Auftrag',
        pinned: 'left',
        width: 160,
        sortable: false,
        // Die Auftragsnummer gilt für ALLE Zeilen des Auftrags und steht nur
        // in der ersten. Damit die Spalte trotzdem wie eine verbundene Zelle
        // wirkt, bekommen ALLE ihre Zellen denselben Hintergrund — sonst
        // schlaegt die Zebra-Streifung des Grids in den leeren Zellen durch und
        // der Block zerfällt optisch in zwei Zeilen.
        //
        // Bewusst ohne AG-Grids rowSpan: Das braucht suppressRowTransform und
        // hat hier nur bei einzelnen Auftraegen gegriffen.
        cellClass: 'bulk-op-order-cell',
        cellRenderer: (p: { data?: SplitGridRow }) => this.renderAuftragCell(p.data),
      },
    ];

    const stagingCols: ColDef<SplitGridRow>[] = [];
    const versandCols: ColDef<SplitGridRow>[] = [];
    const mitgebCols: ColDef<SplitGridRow>[] = [];
    for (const col of m.columns()) {
      const def = this.makeArticleCol(col);
      if (col.isStaging) stagingCols.push(def);
      else if (this.previewResult()!.preview_columns.find((c) => c.id === col.colId)?.kind === 'free_text') mitgebCols.push(def);
      else versandCols.push(def);
    }
    if (stagingCols.length) cols.push({ headerName: 'Staging-Artikel', children: stagingCols });
    if (versandCols.length) cols.push({ headerName: 'Versand-Artikel', children: versandCols });
    if (mitgebCols.length) cols.push({ headerName: 'Mitgeb-Artikel', children: mitgebCols });

    cols.push({
      colId: OperationsBulkExcelComponent.CONFIG_COL_ID,
      headerName: 'Versandkonfiguration',
      pinned: 'right',
      width: 240,
      sortable: false,
      autoHeight: true,
      wrapText: true,
      cellClass: 'bulk-excel-config-cell',
      // Dasselbe Grau wie die Mengenzellen, sobald der Kanal versandt ist —
      // sonst endet die graue Zeile abrupt vor der Config-Spalte.
      cellStyle: (p: { data?: SplitGridRow }) => this.configCellStyle(p.data),
      // „Standard fuer fehlende"-Button wandert in den Spalten-Header.
      headerComponent: ShippingConfigHeaderComponent,
      headerComponentParams: {
        missingCount: () => this.rowsMissingShippingConfig().size,
        onAutoCreate: () => this.openAutoCreateModal(),
      },
      cellRenderer: (p: { data?: SplitGridRow }) => this.renderConfigCell(p.data),
    });

    return cols;
  });

  private makeArticleCol(col: SplitColumn): ColDef<SplitGridRow> {
    return {
      colId: col.colId,
      headerName: col.label,
      minWidth: 110,
      flex: 1,
      sortable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      // Mengen werden ausschliesslich ueber die ▲▼-Pfeile zwischen den Config-
      // Zeilen verschoben — kein manuelles Eintippen (bewusst nicht editierbar).
      valueGetter: (p: ValueGetterParams<SplitGridRow>): number => {
        const m = this.splitModel();
        const data = p.data;
        if (!m || !data) return 0;
        if (data.kind === 'need') return data.row.need_cells?.[col.colId]?.new_quantity ?? 0;
        if (data.kind === 'open') return m.poolFor(this.splitState(), data.row, col);
        return m.cellQty(this.splitState(), data.operationId, data.configRef, col.colId);
      },
      cellRenderer: (p: { data?: SplitGridRow; value?: number }) => this.renderArticleCell(p.data, col, p.value ?? 0),
      cellStyle: (p: { data?: SplitGridRow; value?: number }) => this.articleCellStyle(p.data, col, p.value ?? 0),
    };
  }

  // Kann diese Zelle eine (verschiebbare) Menge halten? = Config-Zeile, deren Typ
  // die Spalte aufnehmen kann und deren Kanal noch nicht versandt ist. Steuert
  // sowohl die ▲▼-Pfeile als auch die Zell-Hintergruende.
  private isCellEditable(data: SplitGridRow | undefined, col: SplitColumn): boolean {
    const m = this.splitModel();
    if (!m || !data || data.kind !== 'config') return false;
    const type = m.configType(data.row, data.configRef, this.pendingShippingConfigs());
    if (!m.canAssign(type, col)) return false;
    if (m.channelShipped(data.row, data.configRef, col)) return false;
    return true;
  }

  // ===========================================================================
  // Cell-Renderer
  // ===========================================================================
  private renderAuftragCell(data: SplitGridRow | undefined): string {
    if (!data || !data.isFirstOfOp) return '';
    const row = data.row;
    // Nur die Auftragsnummer — der Store-Name war meist abgeschnitten/unleserlich.
    if (row.ticket_id && row.order_nr) {
      return `<div class="leading-tight"><a class="bulk-excel-hawk-link" href="/a/ticket/${row.ticket_id}?operation_id=${row.operation_id}" target="_blank" rel="noopener">${this.escapeHtml(row.order_nr)}</a></div>`;
    }
    return `<div class="leading-tight">${this.escapeHtml(row.order_nr ?? String(row.operation_id))}</div>`;
  }

  /**
   * Graut die Config-Spalte aus, sobald der Versand dieser Zeile durch ist.
   * Zeilen sollen durchgehend eine Farbe haben — halb grau, halb weiss liest
   * sich wie ein Darstellungsfehler.
   */
  private configCellStyle(data: SplitGridRow | undefined): Record<string, string> | undefined {
    const m = this.splitModel();
    if (!m || !data || data.kind !== 'config') return undefined;
    if (!m.isCompleted(data.row, data.configRef)) return undefined;

    return { backgroundColor: 'rgba(0,0,0,0.07)' };
  }

  private renderConfigCell(data: SplitGridRow | undefined): string {
    const m = this.splitModel();
    if (!m || !data) return '';
    if (data.kind === 'need') {
      return '<span class="bulk-excel-muted text-xs">Auftragsbedarf (kein Versand)</span>';
    }
    if (data.kind === 'open') {
      const missing = this.rowsMissingShippingConfig().has(data.operationId);
      const hint = missing
        ? '<span class="text-error font-semibold text-xs">⚠ keine Versandkonfig</span>'
        : '<span class="bulk-excel-muted text-xs">offen / nicht zugeordnet</span>';
      // „+ Config" hier nur, wenn es noch keine editierbare Config-Zeile gibt
      // (sonst sitzt der Hinzufuegen-Button auf der letzten Config-Zeile).
      const add = this.hasEditableConfig(data.row)
        ? ''
        : '<span data-action="add" class="text-info cursor-pointer text-xs whitespace-nowrap">+ Config</span>';
      return `<div class="flex items-center gap-2">${hint}${add}</div>`;
    }
    const ref = data.configRef;
    // Abgeschlossene Config: read-only Anzeige, nicht klickbar (kein data-config-ref).
    if (m.isCompleted(data.row, ref)) {
      const doneLabel = this.labelForRef(data.row, ref, undefined);
      return `<div class="flex items-start gap-1 leading-tight py-0.5 bulk-excel-muted"><span class="text-success shrink-0">✓</span><span class="text-xs whitespace-normal break-words">${this.escapeHtml(doneLabel)} <span class="opacity-70">(versandt)</span></span></div>`;
    }
    const key = this.pendingKey(data.operationId, ref);
    const pending = this.pendingShippingConfigs().get(key);
    const isNew = ref.startsWith('new');
    let icon = '';
    if (isNew) icon = '⊕ ';
    else if (pending) icon = '✎ ';
    const label = this.labelForRef(data.row, ref, pending);
    const remove = isNew
      ? `<span data-action="remove" class="shrink-0 text-error cursor-pointer ml-1" title="Config entfernen">✕</span>`
      : '';
    const color = isNew ? 'text-success' : pending ? 'text-info' : '';
    // „+ weitere Config" auf der letzten editierbaren Config-Zeile des Auftrags.
    const editable = this.editableConfigRefs(data.row);
    const isLastEditable = editable[editable.length - 1] === ref;
    // Mit Abstand + Trennstrich abgesetzt, damit "+ weitere Config" nicht
    // versehentlich beim Klick auf die Config getroffen wird.
    const addRow = isLastEditable
      ? '<span data-action="add" class="text-info cursor-pointer text-[11px] whitespace-nowrap block mt-2 pt-1.5 border-t border-base-300">+ weitere Config</span>'
      : '';
    return `<div class="flex flex-col leading-tight py-0.5"><div class="flex items-start justify-between gap-1"><span data-config-ref="${this.escapeHtml(ref)}" class="${color} cursor-pointer text-xs whitespace-normal break-words">${this.escapeHtml(icon)}${this.escapeHtml(label)}</span>${remove}</div>${addRow}</div>`;
  }

  // Artikel-Zellen sind rechtsbuendig (justify-end) → die ▲▼-Controls liegen
  // spaltenweise auf einer Linie.
  private renderArticleCell(data: SplitGridRow | undefined, col: SplitColumn, value: number): string {
    if (!data) return '';
    return `<div class="flex items-center justify-end gap-1 w-full">${this.articleCellInner(data, col, value)}</div>`;
  }

  private articleCellInner(data: SplitGridRow, col: SplitColumn, value: number): string {
    // Auftragsbedarf: reine Anzeige (kein Split, keine Stepper). Der Zuwachs
    // gegenüber dem DB-Stand wird wie bei den anderen Zeilen als „(+X)" gezeigt.
    if (data.kind === 'need') {
      const cell = data.row.need_cells?.[col.colId];
      if (!cell || value === 0) return '<span class="text-base-content/30">–</span>';
      const delta = cell.delta > 0 ? `<span class="text-success/60 text-xs">(+${cell.delta})</span>` : '';
      return `${value}${delta}`;
    }
    if (data.kind === 'open') {
      // Hat der Auftrag von diesem Artikel ueberhaupt eine Menge? Wenn nicht,
      // gibt es nichts zu verteilen → schlichtes „–" statt eines gruenen Hakens.
      const total = data.row.cells[col.colId]?.new_quantity ?? 0;
      if (total === 0) return '<span class="text-base-content/30">–</span>';
      // Menge vorhanden, aber Rest 0 = vollstaendig verteilt → dezenter Haken.
      if (value === 0) return '<span class="text-success/60">✓</span>';
      // Auch hier (vor Config-Anlage) den Excel-Zuwachs „(+X)" zeigen — einheitlich
      // zur Config-Zeile.
      return `<span class="text-warning font-semibold">${value}</span>${this.deltaBadge(data, col, value)}`;
    }

    // Versandte Mengen read-only mit Haken zeigen statt „–". Zwei Fälle, die
    // vorher unterschiedlich aussahen, obwohl sie dasselbe bedeuten:
    //
    //   - teilversandte Config: die VERSAND-Zelle liegt nicht mehr im State
    //     (gleiche Seeding-Regel wie die Matrix, siehe BulkExcelSplitModel)
    //   - abgeschlossene Config: fiel bisher in die normale Darstellung und
    //     zeigte die nackte Zahl, obwohl alles davon versandt ist
    const m = this.splitModel();
    if (m && m.isCompleted(data.row, data.configRef)) {
      if (value > 0) return `<span class="text-success/70">✓ ${value}</span>`;
      return '<span class="text-base-content/30">–</span>';
    }
    if (m && m.channelShipped(data.row, data.configRef, col)) {
      // Teilversand getrennt ausweisen: versandt mit Haken, der zugeordnete
      // Rest daneben. Vorher stand hier nur die Summe, und "3" liess offen, ob
      // davon schon etwas raus ist.
      const { shipped, held } = m.committedSplit(data.row, data.configRef, col.colId);
      if (shipped <= 0 && held <= 0) return '<span class="text-base-content/30">–</span>';

      const versandt = shipped > 0 ? `<span class="text-success/70">✓ ${shipped}</span>` : '';
      const offen = held > 0
        ? `<span class="text-base-content/50 text-xs" title="zugeordnet, noch nicht versandt">${shipped > 0 ? ' · ' : ''}${held} offen</span>`
        : '';
      return `${versandt}${offen}`;
    }

    // Config-Zeile: Wert + (auf der zuweisenden Zeile) der Excel-Zuwachs „(+X)".
    const base = value === 0 ? '<span class="text-base-content/30">–</span>' : `${value}`;
    return `${base}${this.deltaBadge(data, col, value)}${this.renderStepArrows(data, col, value)}`;
  }

  // „(+X)"-Badge fuer den Excel-Zuwachs eines Artikels — PRO ZEILE: aktuelle Menge
  // minus Pre-Import-Baseline (DB-Split der Config bzw. nicht zugeordneter Rest der
  // offen-Zeile). So summieren sich die Badges einer Zeile-Gruppe zum Auftrags-
  // Delta, statt das volle Delta in jeder Config-Zeile zu doppeln. Nur wenn der
  // Artikel im Import ueberhaupt zugenommen hat (Spalten-Delta > 0) und nicht auf
  // abgeschlossenen (read-only) Zeilen.
  private deltaBadge(data: SplitGridRow, col: SplitColumn, value: number): string {
    if (value <= 0) return '';
    const m = this.splitModel();
    if (!m) return '';
    if ((data.row.cells[col.colId]?.delta ?? 0) <= 0) return '';
    let baseline: number;
    if (data.kind === 'open') {
      baseline = m.originalUnassignedQty(data.row, col.colId);
    } else {
      if (m.isCompleted(data.row, data.configRef)) return '';
      baseline = m.originalSplitQty(data.row, data.configRef, col.colId);
    }
    const added = value - baseline;
    if (added <= 0) return '';
    return `<span class="text-success/60 text-xs">(+${added})</span>`;
  }

  // ▲ ▼ neben der Menge (flach nebeneinander, damit es auch in schmalen Spalten
  // in eine Zeile passt): verschiebt eine Einheit in die naechste zuweisbare
  // Config-Zeile darueber/darunter. Pfeil ist blockiert (ausgegraut), wenn keine
  // ansteuerbare Zeile in der Richtung existiert oder nichts zu verschieben ist.
  private renderStepArrows(data: SplitGridRow, col: SplitColumn, value: number): string {
    if (!this.isCellEditable(data, col)) return '';
    // Nichts zu verschieben → keine Pfeile (nur Zellen mit mind. 1 Artikel).
    if (value <= 0) return '';
    // Verschieben ergibt nur Sinn, wenn der Auftrag mindestens zwei Config-Zeilen
    // hat — sonst gibt es kein Ziel. Bei genau einer Config keine Pfeile zeigen.
    if (this.opConfigRefs(data.row).length < 2) return '';
    const floor = this.splitModel()?.floorFor(data.row, data.configRef, col.colId) ?? 0;
    const movable = value > floor; // gesperrte (versandte/gehaltene) Menge bleibt
    const upTarget = movable && this.assignableNeighborRef(data.row, data.configRef, col, 'up') !== null;
    const downTarget = movable && this.assignableNeighborRef(data.row, data.configRef, col, 'down') !== null;
    const btn = (dir: 'up' | 'down', enabled: boolean, glyph: string): string => {
      const cls = enabled ? 'text-base-content/50 hover:text-primary cursor-pointer' : 'text-base-content/15 cursor-default';
      const attr = enabled ? `data-step="${dir}" data-col="${this.escapeHtml(col.colId)}"` : '';
      return `<span ${attr} class="${cls} leading-none select-none" title="${dir === 'up' ? 'eine Zeile nach oben' : 'eine Zeile nach unten'}">${glyph}</span>`;
    };
    return `<span class="inline-flex flex-row items-center gap-0.5 shrink-0 text-[11px]">${btn('up', upTarget, '▲')}${btn('down', downTarget, '▼')}</span>`;
  }

  // Naechste Config-Zeile (in Anzeigereihenfolge) ueber/unter `ref`, die `col`
  // aufnehmen kann (Staging nur auf deckende Config). Nicht-zuweisbare Zeilen
  // werden uebersprungen; gibt null, wenn es in der Richtung keine gibt.
  private assignableNeighborRef(row: ExcelPreviewRow, ref: string, col: SplitColumn, dir: 'up' | 'down'): string | null {
    const m = this.splitModel();
    if (!m) return null;
    const refs = this.opConfigRefs(row);
    const idx = refs.indexOf(ref);
    if (idx < 0) return null;
    const step = dir === 'up' ? -1 : 1;
    for (let i = idx + step; i >= 0 && i < refs.length; i += step) {
      const candidate = refs[i];
      const type = m.configType(row, candidate, this.pendingShippingConfigs());
      if (m.canAssign(type, col) && !m.channelShipped(row, candidate, col)) return candidate;
    }
    return null;
  }

  // Verschiebt eine Einheit von (op, ref, col) in die naechste zuweisbare
  // Nachbar-Config in der angegebenen Richtung.
  private moveCellUnit(data: SplitGridRow, col: SplitColumn, dir: 'up' | 'down'): void {
    const m = this.splitModel();
    if (!m) return;
    const cur = m.cellQty(this.splitState(), data.operationId, data.configRef, col.colId);
    const floor = m.floorFor(data.row, data.configRef, col.colId);
    if (cur - 1 < floor) return; // gesperrte Menge nicht verschieben
    const target = this.assignableNeighborRef(data.row, data.configRef, col, dir);
    if (!target) return;
    let state = withCell(this.splitState(), data.operationId, data.configRef, col.colId, cur - 1);
    const tgtQty = m.cellQty(state, data.operationId, target, col.colId);
    state = withCell(state, data.operationId, target, col.colId, tgtQty + 1);
    this.splitState.set(state);
  }

  private articleCellStyle(data: SplitGridRow | undefined, col: SplitColumn, value: number): Record<string, string> | undefined {
    const m = this.splitModel();
    if (!m || !data) return undefined;
    if (data.kind === 'need') {
      // Gruen markiert eine ÄNDERUNG aus der Datei, nicht "hat überhaupt
      // Bedarf". Sonst leuchtet jede bestehende Bedarfsmenge auf, auch wenn an
      // ihr in diesem Durchlauf nichts angefasst wurde.
      return data.row.need_cells?.[col.colId]?.has_change
        ? { backgroundColor: 'rgba(34, 197, 94, 0.10)' }
        : undefined;
    }
    if (data.kind === 'open') {
      return value > 0 ? { backgroundColor: 'rgba(245, 158, 11, 0.10)' } : undefined;
    }
    if (!this.isCellEditable(data, col)) {
      // Nicht zuweisbar (Staging auf nicht-deckender Config) oder Kanal versandt.
      const type = m.configType(data.row, data.configRef, this.pendingShippingConfigs());
      if (!m.canAssign(type, col)) return { backgroundColor: 'rgba(0,0,0,0.04)' };
      // Versandt → schreibgeschuetzt. Grau wie die übrigen gesperrten Zellen:
      // Ein eigener Blauton las sich wie eine weitere Kategorie neben Staging.
      return { backgroundColor: 'rgba(0,0,0,0.07)' };
    }
    return undefined;
  }

  private labelForRef(row: ExcelPreviewRow, ref: string, pending: ShippingConfigFormData | undefined): string {
    if (pending) return `${this.formDataLabel(pending)}${this.suffixFor(this.formDataMethodLabel(pending))}`;
    const c = this.splitModel()?.allConfigs(row).find((x) => String(x.id) === ref);
    if (!c) return ref;
    return `${c.shipping_type_label || c.shipping_type}${this.suffixFor(this.shippingMethodShortLabel(c))}`;
  }

  // ===========================================================================
  // Grid-Klicks: Config bearbeiten / hinzufuegen / entfernen
  // ===========================================================================
  protected onCellClicked = (event: CellClickedEvent<SplitGridRow>): void => {
    const data = event.data;
    if (!data) return;
    const target = event.event?.target as HTMLElement | undefined;

    // Artikel-Spalten: ▲▼-Stepper-Klick (verschiebt eine Einheit hoch/runter).
    if (event.column.getColId() !== OperationsBulkExcelComponent.CONFIG_COL_ID) {
      const stepEl = target?.closest('[data-step]') as HTMLElement | null;
      if (!stepEl) return;
      const dir = stepEl.dataset['step'] as 'up' | 'down' | undefined;
      const colId = stepEl.dataset['col'];
      if (!dir || !colId) return;
      const col = this.splitColumns().find((c) => c.colId === colId);
      if (col) this.moveCellUnit(data, col, dir);
      return;
    }

    const el = target?.closest('[data-action],[data-config-ref]') as HTMLElement | null;
    if (!el) return;

    if (el.dataset['action'] === 'add') {
      const ref = `new${this.newConfigSeq++}`;
      this.openShippingConfigDialog(data.row, this.pendingKey(data.operationId, ref), null);
      return;
    }
    if (el.dataset['action'] === 'remove') {
      this.removePendingConfig(data.operationId, data.configRef);
      return;
    }
    const ref = el.dataset['configRef'];
    if (!ref) return;
    const base = ref.startsWith('new') ? null
      : (this.splitModel()?.existingConfigs(data.row).find((c) => String(c.id) === ref) ?? null);
    this.openShippingConfigDialog(data.row, this.pendingKey(data.operationId, ref), base);
  };

  private removePendingConfig(opId: number, ref: string): void {
    const map = new Map(this.pendingShippingConfigs());
    map.delete(this.pendingKey(opId, ref));
    this.pendingShippingConfigs.set(map);
    // Zugeordnete Mengen zurueck in den offenen Pool.
    this.splitState.set(withoutRef(this.splitState(), opId, ref));
  }

  // Verteilt den noch offenen Rest je Artikel greedy auf eine (neue) Config —
  // nur Spalten, die der Config-Typ aufnehmen kann.
  private autoAssignOpenTo(row: ExcelPreviewRow, ref: string): void {
    const m = this.splitModel();
    if (!m) return;
    const type = m.configType(row, ref, this.pendingShippingConfigs());
    let state = this.splitState();
    for (const col of m.columns()) {
      if (!m.canAssign(type, col)) continue;
      const pool = m.poolFor(state, row, col);
      if (pool <= 0) continue;
      const current = m.cellQty(state, row.operation_id, ref, col.colId);
      state = withCell(state, row.operation_id, ref, col.colId, current + pool);
    }
    this.splitState.set(state);
  }

  // Entfernt Staging-Zuordnungen einer Config, die durch einen Typ-Wechsel nicht
  // mehr deckend ist (Mengen wandern zurueck in den offenen Pool).
  private cleanupRefAssignability(row: ExcelPreviewRow, ref: string): void {
    const m = this.splitModel();
    if (!m) return;
    const type = m.configType(row, ref, this.pendingShippingConfigs());
    let state = this.splitState();
    for (const col of m.columns()) {
      if (m.canAssign(type, col)) continue;
      if (m.cellQty(state, row.operation_id, ref, col.colId) > 0) {
        state = withCell(state, row.operation_id, ref, col.colId, 0);
      }
    }
    this.splitState.set(state);
  }

  // ===========================================================================
  // Versand-Config-Editor-Dialog (geteilte Form-Komponente)
  // ===========================================================================
  protected editingShippingRow = signal<ExcelPreviewRow | null>(null);
  protected editingShippingContext = signal<ShippingConfigContext | null>(null);
  protected editingPendingKey = signal<string | null>(null);
  protected editingShippingBaseConfig = signal<ShippingConfig | null>(null);

  private async openShippingConfigDialog(row: ExcelPreviewRow, pendingKey: string, base: ShippingConfig | null): Promise<void> {
    this.editingShippingRow.set(row);
    this.editingPendingKey.set(pendingKey);
    this.editingShippingBaseConfig.set(base);
    this.editingShippingContext.set(null);
    try {
      this.editingShippingContext.set(await this.shippingConfigService.getContext(row.operation_id));
    } catch (e) {
      console.error('Versand-Kontext konnte nicht geladen werden', e);
      this.editingShippingContext.set({ customer_has_return_address: false });
    }
  }

  protected closeShippingConfigDialog(): void {
    this.editingShippingRow.set(null);
    this.editingPendingKey.set(null);
    this.editingShippingBaseConfig.set(null);
    this.editingShippingContext.set(null);
  }

  protected editingInitialConfig = computed<ShippingConfig | null>(() => {
    const key = this.editingPendingKey();
    if (!key) return null;
    const base = this.editingShippingBaseConfig();
    const pending = this.pendingShippingConfigs().get(key);
    if (pending) return this.formDataAsConfig(pending, base);
    return base;
  });

  // Form-Save persistiert NICHT sofort — die Aenderung wird als pending-Eintrag
  // gehalten und erst beim Apply geschrieben. Neue Configs verteilen den offenen
  // Rest automatisch; Typ-Wechsel raeumt nicht mehr zuweisbare Mengen auf.
  protected onShippingConfigSave(data: ShippingConfigFormData): void {
    const key = this.editingPendingKey();
    const row = this.editingShippingRow();
    if (!key || !row) return;
    const wasNew = !this.pendingShippingConfigs().has(key) && key.includes('#new');
    const map = new Map(this.pendingShippingConfigs());
    map.set(key, data);
    this.pendingShippingConfigs.set(map);

    const ref = key.slice(key.indexOf('#') + 1);
    if (wasNew) this.autoAssignOpenTo(row, ref);
    else this.cleanupRefAssignability(row, ref);

    this.closeShippingConfigDialog();
  }

  private formDataAsConfig(form: ShippingConfigFormData, base: ShippingConfig | null): ShippingConfig {
    return {
      id: base?.id ?? 0,
      operation_id: base?.operation_id ?? 0,
      shipping_type: form.shipping_type,
      shipping_type_label: base?.shipping_type_label,
      shipment_method: form.shipment_method,
      package_speed: form.package_speed ?? undefined,
      article_disposition: form.article_disposition,
      shipping_date: form.shipping_date ?? undefined,
      pickpoint_station_id: form.pickpoint_station_id ?? undefined,
      pickpoint_station: form.pickpoint_station_id === base?.pickpoint_station_id ? base?.pickpoint_station : undefined,
      target_user_id: form.target_user_id ?? undefined,
      target_user_name: form.target_user_id === base?.target_user_id ? base?.target_user_name : undefined,
      target_warehouse_id: form.target_warehouse_id ?? undefined,
      target_warehouse: form.target_warehouse_id === base?.target_warehouse_id ? base?.target_warehouse : undefined,
      // Regel-/Offset-Felder MUSS durchgereicht werden, sonst zeigt die Form beim
      // Bearbeiten einer (auto-)erzeugten Config faelschlich "Fester Techniker" /
      // festes Datum statt der Regel "Vom Auftrag" / "X Werktage vor Auftrag".
      target_user_from_operation: form.target_user_from_operation ?? undefined,
      shipping_date_offset_days: form.shipping_date_offset_days ?? undefined,
      // Versandfenster mit durchreichen — sonst zeigt der Editor „X Werktage
      // vor Auftrag" statt „Zeitraum vor Auftrag" und ein Übernehmen würde das
      // Fenster aus dem pending-Eintrag löschen.
      earliest_shipping_offset_days: form.earliest_shipping_offset_days ?? undefined,
      staging_ship_offset_days: form.staging_ship_offset_days ?? undefined,
      staging_ship_ready_at: form.staging_ship_ready_at ?? undefined,
      technician_ship_target: form.technician_ship_target ?? undefined,
      target_customer_id: form.target_customer_id ?? undefined,
      target_customer_name: form.target_customer_id === base?.target_customer_id ? base?.target_customer_name : undefined,
      notes: form.notes,
      pm_code: form.pm_code ?? undefined,
      pickup_date: form.pickup_date ?? null,
    };
  }

  // Label-Helfer (geteilt mit dem Config-Cell-Renderer) -----------------------
  private suffixFor(method: string): string {
    return method ? ` (${method})` : '';
  }

  private formDataLabel(form: ShippingConfigFormData): string {
    switch (form.shipping_type) {
      case 'pickpoint': return 'PickPoint';
      case 'store_shipping': return form.article_disposition === 'consumed' ? 'Filialversand (verbraucht)' : 'Filialversand';
      case 'technician_shipping': return 'Technikerversand';
      case 'customer_shipping': return 'Kunden-Rückversand';
      default: return form.shipping_type;
    }
  }

  private formDataMethodLabel(form: ShippingConfigFormData): string {
    const map: Record<string, string> = { parcel: 'Paket', pallet: 'Palette', courier: 'Kurier', pickup: 'Abholung' };
    const mth = map[form.shipment_method] ?? '';
    if (!mth) return '';
    if (form.shipment_method === 'parcel' && form.package_speed === 'express') return `${mth} Express`;
    return mth;
  }

  private shippingMethodShortLabel(sc: ShippingConfig): string {
    const map: Record<string, string> = { parcel: 'Paket', pallet: 'Palette', courier: 'Kurier', pickup: 'Abholung' };
    const mth = sc.shipment_method ? map[sc.shipment_method] : '';
    if (!mth) return '';
    if (sc.shipment_method === 'parcel' && sc.package_speed === 'express') return `${mth} Express`;
    return mth;
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }

  // ===========================================================================
  // Auto-Create-Modal (Standard-Configs fuer alle fehlenden anlegen)
  // ===========================================================================
  protected showAutoCreateModal = signal<boolean>(false);
  // Default-Vorlaufzeiten (WERKTAGE vor Auftragsdatum) je Gruppe. Initialwerte =
  // Fallback, falls der Backend-Load scheitert; sonst werden sie in ngOnInit aus
  // GROUP_SHIPPING_OFFSET_DAYS ueberschrieben (Single Source of Truth).
  // „bis" = spätester Versandtag (Pflicht, 0 = am Auftragstag).
  protected leadTimeInternalDays = signal<number>(0);
  protected leadTimeExternalMaterialDays = signal<number>(0);
  protected leadTimeExternalStagingDays = signal<number>(8);
  protected leadTimeExternalOtherDays = signal<number>(8);
  // „von" = frühester Versandtag des Versandfensters „Zeitraum vor Auftrag";
  // null/leer = kein Fenster → reine „X Werktage vor Auftrag"-Regel. Muss,
  // wenn gesetzt, >= „bis" sein (mehr Werktage vor Auftrag = früherer Tag).
  protected earliestLeadTimeInternalDays = signal<number | null>(null);
  protected earliestLeadTimeExternalMaterialDays = signal<number | null>(null);
  protected earliestLeadTimeExternalStagingDays = signal<number | null>(10);
  protected earliestLeadTimeExternalOtherDays = signal<number | null>(10);
  // Fenster-Plausibilität je Gruppe: „von" gesetzt, aber < „bis" → invalide.
  protected leadTimeRangesInvalid = computed(() => {
    const invalid = (von: number | null, bis: number) => von != null && Number(von) < Number(bis);
    return invalid(this.earliestLeadTimeInternalDays(), this.leadTimeInternalDays())
      || invalid(this.earliestLeadTimeExternalStagingDays(), this.leadTimeExternalStagingDays())
      || invalid(this.earliestLeadTimeExternalMaterialDays(), this.leadTimeExternalMaterialDays())
      || invalid(this.earliestLeadTimeExternalOtherDays(), this.leadTimeExternalOtherDays());
  });
  // Typ-Zuordnung je externem Scope ('technician' | 'store') — vom Select im
  // Auto-Create-Dialog. Defaults wie bisher: Staging → Techniker, Material/
  // Standard → Filiale.
  protected assignExternalStaging = signal<'technician' | 'store'>('technician');
  protected assignExternalMaterial = signal<'technician' | 'store'>('store');
  protected assignExternalOther = signal<'technician' | 'store'>('store');
  // Klonen der zuletzt versandten Config (Nachversand) — Default an, abwaehlbar.
  protected cloneArchivedConfigs = signal<boolean>(true);
  protected autoCreateRunning = signal<boolean>(false);
  protected autoCreateError = signal<string | null>(null);

  protected openAutoCreateModal(): void {
    if (this.rowsMissingShippingConfig().size === 0) return;
    this.autoCreateError.set(null);
    this.showAutoCreateModal.set(true);
  }

  protected closeAutoCreateModal(): void {
    if (this.autoCreateRunning()) return;
    this.showAutoCreateModal.set(false);
  }

  protected async applyAutoCreate(): Promise<void> {
    const missingIds = Array.from(this.rowsMissingShippingConfig());
    if (missingIds.length === 0) return;

    this.autoCreateRunning.set(true);
    this.autoCreateError.set(null);
    try {
      const pendingStagingOpIds = this.categorizedMissingRows().external_staging.map((r) => r.operation_id);
      const previews = await this.shippingConfigService.previewDefaults(missingIds, {
        internal: this.leadTimeInternalDays(),
        external_staging: this.leadTimeExternalStagingDays(),
        external_material: this.leadTimeExternalMaterialDays(),
        external_other: this.leadTimeExternalOtherDays(),
      }, {
        internal: this.earliestLeadTimeInternalDays(),
        external_staging: this.earliestLeadTimeExternalStagingDays(),
        external_material: this.earliestLeadTimeExternalMaterialDays(),
        external_other: this.earliestLeadTimeExternalOtherDays(),
      }, pendingStagingOpIds, {
        assignments: {
          external_staging: this.assignExternalStaging(),
          external_material: this.assignExternalMaterial(),
          external_other: this.assignExternalOther(),
        },
        cloneArchived: this.cloneArchivedConfigs(),
      });

      const rowsById = new Map(this.previewResult()!.preview_rows.map((r) => [r.operation_id, r]));
      const map = new Map(this.pendingShippingConfigs());
      const created: Array<{ row: ExcelPreviewRow; ref: string }> = [];
      for (const preview of previews) {
        const ref = `new${this.newConfigSeq++}`;
        map.set(this.pendingKey(preview.operation_id, ref), this.previewToFormData(preview));
        const row = rowsById.get(preview.operation_id);
        if (row) created.push({ row, ref });
      }
      this.pendingShippingConfigs.set(map);
      // Nach dem Setzen der pending-Map den offenen Rest je neuer Config verteilen.
      for (const { row, ref } of created) this.autoAssignOpenTo(row, ref);
      this.showAutoCreateModal.set(false);
    } catch (e) {
      console.error('Standard-Configs konnten nicht berechnet werden', e);
      this.autoCreateError.set('Backend-Aufruf fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      this.autoCreateRunning.set(false);
    }
  }

  private previewToFormData(preview: ShippingConfigDefaultPreview): ShippingConfigFormData {
    const f = preview.form_data;
    return {
      shipping_type: f.shipping_type,
      shipment_method: f.shipment_method,
      package_speed: f.package_speed ?? null,
      article_disposition: f.article_disposition ?? 'technician_transfer',
      shipping_date: f.shipping_date ?? null,
      pickpoint_station_id: f.pickpoint_station_id ?? null,
      target_user_id: f.target_user_id ?? null,
      target_warehouse_id: f.target_warehouse_id ?? null,
      // Regel-/Offset-Felder aus preview_attrs uebernehmen — sonst defaultet die
      // Config faelschlich auf "Fester Techniker" / festes Datum.
      target_user_from_operation: f.target_user_from_operation ?? undefined,
      shipping_date_offset_days: f.shipping_date_offset_days ?? null,
      // Versandfenster „Zeitraum vor Auftrag" (von-Wert aus dem Dialog) —
      // ohne Übernahme würde das Fenster hier stillschweigend verworfen.
      earliest_shipping_offset_days: f.earliest_shipping_offset_days ?? null,
      staging_ship_offset_days: f.staging_ship_offset_days ?? null,
      staging_ship_ready_at: f.staging_ship_ready_at ?? null,
      technician_ship_target: f.technician_ship_target ?? null,
      target_customer_id: f.target_customer_id ?? null,
      pickup_date: f.pickup_date ?? null,
      notes: f.notes ?? undefined,
      pm_code: f.pm_code ?? null,
    };
  }

  protected categorizedMissingRows = computed<Record<AutoCreateCategory, ExcelPreviewRow[]>>(() => {
    const buckets: Record<AutoCreateCategory, ExcelPreviewRow[]> = {
      internal: [], external_staging: [], external_material: [], external_other: [],
    };
    const result = this.previewResult();
    if (!result) return buckets;
    const missing = this.rowsMissingShippingConfig();
    for (const row of result.preview_rows) {
      if (!missing.has(row.operation_id)) continue;
      buckets[this.categorizeRow(row)].push(row);
    }
    return buckets;
  });

  private categorizeRow(row: ExcelPreviewRow): AutoCreateCategory {
    if (row.is_internal) return 'internal';
    const hasStaging = Object.values(row.cells).some((c) => c.action_type === 'STAGING' && c.new_quantity > 0);
    if (hasStaging) return 'external_staging';
    if (row.priority_material_shipping) return 'external_material';
    return 'external_other';
  }

  // ===========================================================================
  // Diff-/Apply-Auswertung
  // ===========================================================================
  protected validChanges = computed<ExcelChange[]>(() => {
    const r = this.previewResult();
    return r ? r.changes.filter((c) => !c.error) : [];
  });

  protected invalidChanges = computed<ExcelChange[]>(() => {
    const r = this.previewResult();
    return r ? r.changes.filter((c) => !!c.error) : [];
  });

  protected changedRowsCount = computed<number>(() => {
    const r = this.previewResult();
    return r ? r.preview_rows.filter((row) => row.has_change).length : 0;
  });

  protected totalRowsCount = computed<number>(() => {
    const r = this.previewResult();
    return r ? r.preview_rows.length : 0;
  });

  protected hasParsingTakeovers = computed<boolean>(() => {
    const r = this.previewResult();
    if (!r) return false;
    return r.preview_rows.some((row) => Object.values(row.cells).some((c) => c.is_parsing_takeover));
  });

  protected pendingShippingConfigsCount = computed<number>(() => this.pendingShippingConfigs().size);

  protected totalApplyCount = computed<number>(() => this.validChanges().length + this.pendingShippingConfigsCount());

  // Aufträge mit noch offenem (nicht zugeordnetem) Rest, aber ohne editierbare
  // (offene/pending) Versandkonfig. Abgeschlossene Configs zaehlen NICHT — sie
  // koennen nichts mehr aufnehmen, also fehlt fuer den offenen Rest weiterhin
  // eine aktive Config (Nachversand-Fall). Vollstaendig versandte Aufträge ohne
  // offenen Rest fallen raus.
  protected rowsMissingShippingConfig = computed<Set<number>>(() => {
    const m = this.splitModel();
    const result = this.previewResult();
    if (!m || !result) return new Set<number>();
    const state = this.splitState();
    const ids = new Set<number>();
    for (const row of result.preview_rows) {
      const anyPool = m.columns().some((col) => m.poolFor(state, row, col) > 0);
      if (!anyPool) continue;
      if (!this.hasEditableConfig(row)) ids.add(row.operation_id);
    }
    return ids;
  });

  // Fehlende Aufträge, die eine bereits archivierte (versandte) Config haben →
  // Kandidaten fuers Klonen der zuletzt versendeten Konfiguration (Nachversand).
  // Steuert Sichtbarkeit + „(N Aufträge betroffen)" der Klon-Checkbox.
  protected cloneEligibleCount = computed<number>(() => {
    const result = this.previewResult();
    if (!result) return 0;
    const missing = this.rowsMissingShippingConfig();
    let n = 0;
    for (const row of result.preview_rows) {
      if (!missing.has(row.operation_id)) continue;
      const configs = [...(row.shipping_configs ?? []), ...(row.completed_shipping_configs ?? [])];
      if (configs.some((c) => c.versand_shipped)) n++;
    }
    return n;
  });

  protected canApply = computed<boolean>(() =>
    this.totalApplyCount() > 0 && this.rowsMissingShippingConfig().size === 0,
  );

  // ===========================================================================
  // Grid-Hoehe
  // ===========================================================================
  private readonly MAX_VISIBLE_ROWS = 14;
  // Reserve fuer Gruppen-Header (Staging/Versand/Mitgeb) + mehrzeilige Spaltenheader.
  private readonly HEADER_PX = 130;
  private readonly ROW_PX = 34;

  protected gridDomLayout = computed<'normal' | 'autoHeight'>(() =>
    this.splitRows().length <= this.MAX_VISIBLE_ROWS ? 'autoHeight' : 'normal',
  );

  protected gridHeightStyle = computed<string>(() => {
    if (this.gridDomLayout() === 'autoHeight') return 'auto';
    return `${this.HEADER_PX + this.MAX_VISIBLE_ROWS * this.ROW_PX + 8}px`;
  });

  // ===========================================================================
  // Lifecycle / Projekte / Export / Upload
  // ===========================================================================
  ngOnInit(): void {
    this.selectedProjectId.set(this.projectId);
    this.loadProjects();
    this.loadGroupOffsetDefaults();
  }

  // Default-Vorlaufzeiten je Auftragsgruppe aus dem Backend laden
  // (GROUP_SHIPPING_OFFSET_DAYS) — haelt das Auto-Create-Modal driftfrei zur
  // Backend-Regel. Bei Fehler bleiben die Fallback-Initialwerte der Signale.
  private async loadGroupOffsetDefaults(): Promise<void> {
    try {
      const d = await this.shippingConfigService.getGroupOffsetDefaults();
      if (d.offsets['internal'] != null) this.leadTimeInternalDays.set(d.offsets['internal']);
      if (d.offsets['external_material'] != null) this.leadTimeExternalMaterialDays.set(d.offsets['external_material']);
      if (d.offsets['external_staging'] != null) this.leadTimeExternalStagingDays.set(d.offsets['external_staging']);
      if (d.offsets['external_other'] != null) this.leadTimeExternalOtherDays.set(d.offsets['external_other']);
      // „von"-Defaults: fehlende Gruppen = bewusst kein Fenster (null).
      this.earliestLeadTimeInternalDays.set(d.earliest['internal'] ?? null);
      this.earliestLeadTimeExternalMaterialDays.set(d.earliest['external_material'] ?? null);
      this.earliestLeadTimeExternalStagingDays.set(d.earliest['external_staging'] ?? null);
      this.earliestLeadTimeExternalOtherDays.set(d.earliest['external_other'] ?? null);
    } catch (err) {
      console.error('Gruppen-Vorlaufzeiten konnten nicht geladen werden', err);
    }
  }

  private loadProjects(): void {
    this.projectService.getActive().subscribe({
      next: (active) => {
        const filtered = active
          .filter((p) => !(p.name || '').startsWith('[INTERN]'))
          .map((p) => ({ id: p.id, name: p.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.projects.set(filtered);
      },
      error: (err) => {
        console.error('Projekte konnten nicht geladen werden', err);
        this.errorMessage.set('Projektliste konnte nicht geladen werden.');
      },
    });
  }

  protected close(): void {
    this.closeDialog.emit();
  }

  protected canExport(): boolean {
    return this.selectedProjectId() != null && this.operationIds.length > 0;
  }

  protected async onExport(): Promise<void> {
    const projectId = this.selectedProjectId();
    if (projectId == null) return;

    this.errorMessage.set(null);
    try {
      const blob = await this.excelService.exportExcel(this.operationIds, projectId, this.templateSource());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Versand_Bulk_${projectId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      this.phase.set('uploading');
    } catch (err) {
      console.error('Excel-Export fehlgeschlagen', err);
      this.errorMessage.set('Export fehlgeschlagen — bitte erneut versuchen.');
    }
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    this.uploadedFile.set(file);
  }

  protected async onUpload(): Promise<void> {
    const file = this.uploadedFile();
    if (!file || this.uploadRunning()) return; // Doppel-Klick → kein zweiter Request

    this.uploadRunning.set(true);
    this.errorMessage.set(null);
    this.previewResult.set(null);
    try {
      const result = await this.excelService.importExcelPreview(file);
      this.previewResult.set(result);
      // Split-State aus den bestehenden Configs + Greedy-Default initialisieren.
      this.pendingShippingConfigs.set(new Map());
      this.splitState.set(new BulkExcelSplitModel(result).initialState(new Map()));
      this.phase.set('review');
    } catch (err) {
      console.error('Excel-Preview fehlgeschlagen', err);
      this.errorMessage.set('Datei konnte nicht gelesen werden.');
    } finally {
      this.uploadRunning.set(false);
    }
  }

  protected backToUpload(): void {
    this.phase.set('uploading');
    this.previewResult.set(null);
    this.splitState.set(new Map());
    this.pendingShippingConfigs.set(new Map());
  }

  // ===========================================================================
  // Apply (atomar: Mengen + Configs + Split in EINEM Request)
  // ===========================================================================
  protected async onApply(): Promise<void> {
    if (!this.canApply()) return;
    this.phase.set('applying');
    this.errorMessage.set(null);
    try {
      const { configs, split } = this.buildApplyPayload();
      const result = await this.excelService.importExcelApplyAtomic(this.validChanges(), configs, split);
      // created/updated zaehlen nur Requirements — Config-Anlagen/-Updates separat
      // dazurechnen, damit der Done-Screen den Gesamtumfang spiegelt.
      this.applyResult.set({
        created: result.created + (result.config_created ?? 0),
        updated: result.updated + (result.config_updated ?? 0),
        deleted: result.deleted,
        errors: result.errors,
      });
      this.phase.set('done');
      if (result.success) {
        this.imported.emit();
        this.pendingShippingConfigs.set(new Map());
      }
    } catch (err) {
      console.error('Excel-Apply fehlgeschlagen', err);
      // Backend-Fehlermeldung (z.B. die Σ-/Bedarf-Validierung) sichtbar machen,
      // damit bei einem Live-Fehler nachvollziehbar ist, was schiefging.
      this.errorMessage.set(this.importErrorText(err));
      this.phase.set('review');
    }
  }

  // Liest die Backend-Fehlermeldung aus einer fehlgeschlagenen HTTP-Antwort
  // (422 → { error: "..." }) und faellt sonst auf einen generischen Text zurueck.
  // Robust gegen verschiedene Body-Formen: HttpErrorResponse.error kann ein
  // bereits geparstes Objekt ODER der rohe JSON-/Text-Body (String) sein.
  private importErrorText(err: unknown): string {
    const fallback = 'Import fehlgeschlagen — bitte erneut versuchen.';
    const e = err as { error?: unknown; message?: string } | null;
    if (!e) return fallback;

    let body: unknown = e.error;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch { /* bleibt ein reiner Text-Body */ }
    }
    const fromObject = (typeof body === 'object' && body !== null)
      ? ((body as { error?: string; message?: string }).error
         ?? (body as { error?: string; message?: string }).message)
      : undefined;
    const msg = fromObject ?? (typeof body === 'string' ? body : undefined) ?? e.message;
    return msg ? `Import fehlgeschlagen: ${msg}` : fallback;
  }

  // Baut die Config- + Split-Payloads fuer den atomaren Apply.
  private buildApplyPayload(): { configs: ExcelApplyConfigSpec[]; split: ExcelApplySplitGroup[] } {
    const configs: ExcelApplyConfigSpec[] = [];
    for (const [key, formData] of this.pendingShippingConfigs()) {
      const hashIdx = key.indexOf('#');
      const opId = Number(key.slice(0, hashIdx));
      const ref = key.slice(hashIdx + 1);
      if (ref.startsWith('new')) {
        configs.push({ ref: key, operation_id: opId, ...formData });
      } else {
        configs.push({ operation_id: opId, config_id: Number(ref), ...formData });
      }
    }

    const m = this.splitModel();
    const rowsById = new Map(this.previewResult()!.preview_rows.map((r) => [r.operation_id, r]));
    const colById = new Map(this.splitColumns().map((c) => [c.colId, c]));
    const split: ExcelApplySplitGroup[] = [];
    for (const [opId, byRef] of this.splitState()) {
      const row = rowsById.get(opId);
      for (const [ref, inner] of byRef) {
        // Abgeschlossene Configs sind read-only (bereits versandt) — ihre Spur
        // nicht erneut als Split schreiben.
        if (row && m?.isCompleted(row, ref)) continue;
        // config_ref: Temp-Key fuer neue Configs, sonst die bestehende config_id.
        const configRef = ref.startsWith('new') ? this.pendingKey(opId, ref) : ref;
        const requirements: ExcelApplySplitGroup['requirements'] = [];
        for (const [colId, qty] of inner) {
          if (qty <= 0) continue;
          const col = colById.get(colId);
          if (!col) continue;
          const entry: ExcelApplySplitGroup['requirements'][number] = {
            action_type: col.isStaging ? 'STAGING' : 'VERSAND',
            quantity: qty,
          };
          if (colId.startsWith('t:')) entry.template_id = Number(colId.slice(2));
          else entry.free_text = colId.slice(2);
          requirements.push(entry);
        }
        // Jede bekannte Config-Spur als Gruppe senden (auch leer → Split leeren).
        split.push({ operation_id: opId, config_ref: configRef, requirements });
      }
    }
    return { configs, split };
  }
}
